<template>
  <el-card
    id="bankSection"
    class="mainCard clear-card is-hover-shadow"
  >
    <!-- Title -->
    <div
      class="title"
      :class="{
        noMargin: requestMode === 'post',
      }"
    >
      <div class="left">
        {{ $t('coach.invoice.settings.bankDetails') }}
        <span class="thinTitle">
          {{ $t('coach.invoice.settings.mustBeFilled') }}
        </span>
      </div>
      <div
        v-if="requestMode === 'patch'"
        class="right"
      >
        <el-button
          :type="edit ? 'plain' : 'primary'"
          round
          @click="clickEdit"
        >
          <span v-if="!edit">
            {{ $t('common.edit') }} <i class="el-icon-edit"></i>
          </span>
          <span v-else>
            {{ $t('common.save') }}
          </span>
        </el-button>
      </div>
    </div>

    <!-- Content -->
    <el-collapse-transition>
      <div
        v-show="requestMode === 'patch'"
        class="content"
      >
        <el-form
          ref="settingsForm"
          :model="invoice.bank_detail"
          :rules="rules"
        >
          <!-- ROW 1 -->
          <el-row :gutter="20">
            <!-- Name -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.bankName')"
                prop="name"
                :error="error.name[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.name"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.name', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <!-- Swift code -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.bankSwift')"
                prop="swift_code"
                :error="error.swift_code[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.swift_code"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.swift_code', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <!-- IBAN -->
            <div style="width: 100%;">
              <el-col :span="responsiveRow.two">
                <el-form-item
                  :label="$t('coach.invoice.settings.iban')"
                  prop="iban"
                  :error="error.iban[0]"
                >
                  <el-input
                    v-model="invoice.bank_detail.iban"
                    :disabled="!edit"
                    :suffix-icon="edit ? 'el-icon-edit' : ''"
                    @change="syncUpdateData('bank_detail.iban', ...arguments)"
                  />
                </el-form-item>
              </el-col>
            </div>
            <!-- Account number -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.beneficiaryAccount')"
                prop="account_number"
                :error="error.account_number[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.account_number"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.account_number', ...arguments)"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- ROW 2 -->
          <el-row :gutter="20">
            <!-- Address line 1 -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.addressLine1')"
                prop="address_line1"
                :error="error.address_line1[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.address_line1"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.address_line1', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <!-- Address line 2 -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.addressLine2')"
                prop="address_line2"
                :error="error.address_line2[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.address_line2"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.address_line2', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <!-- Location - Country -->
            <el-col :span="responsiveRow.two">
              <!-- String mode -->
              <el-form-item
                :label="$t('common.country')"
              >
                <el-tooltip
                  class="labelTooltip"
                  effect="dark"
                  placement="top"
                  :content="$t('coach.invoice.settings.countryTooltip')"
                >
                  <i class="el-icon-info"></i>
                </el-tooltip>
                <el-input
                  :disabled="true"
                  :value="invoiceCountryName"
                />
              </el-form-item>
            </el-col>
            <!-- Location - Region -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.stateLabel')"
                prop="state"
                :error="error.state[0]"
              >
                <div v-if="!edit">
                  <el-input
                    v-if="invoice.bank_detail.location_data.region_name"
                    :value="invoice.bank_detail.location_data.region_name"
                    disabled
                  />
                  <i
                    v-else
                    class="emptyPlaceholder"
                  >
                    -
                  </i>
                </div>
                <el-select
                  v-else
                  v-model="invoiceRegion"
                  allow-create
                  default-first-option
                  :placeholder="$t('common.select')"
                  filterable
                  value-key="id"
                >
                  <el-option
                    v-for="region in regions"
                    :key="`Region${region.id}`"
                    :label="region.name"
                    :value="region"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <!-- Location - City -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.city')"
                prop="city"
                :error="error.city[0]"
              >
                <div v-if="!edit">
                  <el-input
                    v-if="invoice.bank_detail.location_data.city_name"
                    :value="invoice.bank_detail.location_data.city_name"
                    disabled
                  />
                  <i
                    v-else
                    class="emptyPlaceholder"
                  >
                    -
                  </i>
                </div>
                <el-select
                  v-else
                  v-model="invoiceCity"
                  allow-create
                  default-first-option
                  :placeholder="$t('common.select')"
                  filterable
                  value-key="id"
                >
                  <el-option
                    v-for="city in cities"
                    :key="`City${city.id}`"
                    :label="city.name"
                    :value="city"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <!-- Postal code -->
            <el-col :span="responsiveRow.two">
              <el-form-item
                :label="$t('coach.invoice.settings.postalLabel')"
                prop="postal_code"
                :error="error.postal_code[0]"
              >
                <el-input
                  v-model="invoice.bank_detail.postal_code"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('bank_detail.postal_code', ...arguments)"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-collapse-transition>
  </el-card>
</template>

<script>
import api from '@/api';
import apiRules from '@/mixins/apiRules';

export default {
  mixins: [
    apiRules,
  ],

  props: {
    roleType: {
      type: String,
      default: null,
    },

    editFlag: {
      type: Object,
      default: null,
    },

    responsiveRow: {
      type: Object,
      default: () => ({
        one: 24,
        two: 24,
      }),
    },

    requestMode: {
      type: String,
      default: 'post',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    invoice: {
      type: Object,
      default: null,
    },

    countries: {
      type: Array,
      default: null,
    },

    savedFlag: {
      type: Boolean,
      default: false,
    },
  },

  // Arrow style => not work with rules validator
  data() {
    return {
      updateData: {},
      regions: [],
      cities: [],
      rules: {
        name: [
          {
            required: true,
            message: this.$t('coach.invoice.settings.rules.name'),
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
        ],
        swift_code: [
          {
            required: true,
            message: this.$t('coach.invoice.settings.rules.swift'),
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
        ],
        account_number: [
          {
            required: true,
            message: this.$t('coach.invoice.settings.rules.accountNumber'),
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
        ],
        address_line1: [
          {
            required: true,
            message: `${this.$t('coach.invoice.settings.rules.address')} 1`,
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
        ],
        address_line2: [
          {
            required: false,
            message: `${this.$t('coach.invoice.settings.rules.address')} 2`,
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
        ],
        postal_code: [
          {
            required: false,
            message: this.$t('coach.invoice.settings.rules.postal'),
            trigger: 'blur',
          },
          {
            max: 255,
            message: this.$t('coach.invoice.settings.rules.noMoreThan255'),
            trigger: 'blur',
          },
          // {
          //   validator: (rule, value, callback) => {
          //     const regex = /^[0-9]*$/;
          //     if (!regex.test(value)) {
          //       callback(new Error('Postal code must be a number'));
          //     } else {
          //       callback();
          //     }
          //   },
          //   trigger: ['blur', 'change'],
          // },
        ],
        country: [
          {
            validator: (rule, value, callback) => {
              if (this.invoice.bank_detail.location_data.country_id > 0) {
                callback();
              } else {
                callback(new Error(this.$t('coach.invoice.settings.rules.country')));
              }
            },
            trigger: ['blur', 'change'],
          },
        ],
        city: [
          {
            validator: (rule, value, callback) => {
              if (this.invoice.bank_detail.location_data.city_id > 0
                || (this.invoice.bank_detail.location_data.city_name
                  && this.invoice.bank_detail.location_data.city_name.length < 255)) {
                callback();
              } else if (this.invoice.bank_detail.location_data.city_name.length >= 255) {
                callback(new Error(this.$t('coach.invoice.settings.rules.noMoreThan255')));
              } else {
                callback(new Error(this.$t('coach.invoice.settings.rules.city')));
              }
            },
            trigger: ['blur', 'change'],
          },
        ],
        state: [
          {
            validator: (rule, value, callback) => {
              if (this.invoice.bank_detail.location_data.region_id > 0
                || (this.invoice.bank_detail.location_data.region_name
                  && this.invoice.bank_detail.location_data.region_name.length < 255)) {
                callback();
              } else if (this.invoice.bank_detail.location_data.region_name.length >= 255) {
                callback(new Error(this.$t('coach.invoice.settings.rules.noMoreThan255')));
              } else {
                callback(new Error(this.$t('coach.invoice.settings.rules.state')));
              }
            },
            trigger: ['blur', 'change'],
          },
        ],
      },
    };
  },

  computed: {
    error() {
      const errors = {
        name: [],
        swift_code: [],
        iban: [],
        account_number: [],
        address_line1: [],
        address_line2: [],
        country: [],
        city: [],
        state: [],
        postal_code: [],
      };

      if (this.apiRules?.bank_detail && Object.entries(this.apiRules.bank_detail).length > 0) {
        Object.keys(this.apiRules.bank_detail).forEach((key) => {
          errors[key] = this.apiRules.bank_detail[key];
        });
      }

      return errors;
    },

    edit: {
      get() {
        return this.editFlag.bank_detail;
      },
      set(newValue) {
        if (newValue) {
          this.editFlag.bank_detail = newValue;
          this.editFlag.invoice = false;
          this.editFlag.beneficiary = false;
          // this.$emit('update:editFlag.invoice', newValue);
          // this.$emit('update:editFlag.beneficiary', false);
          // this.$emit('update:editFlag.bank_detail', false);
        } else {
          this.editFlag.bank_detail = newValue;
          // this.$emit('update:editFlag.invoice', newValue);
        }
      },
    },

    invoiceCountryName() {
      let name = '-';

      if (this.invoice.bank_detail.location_data.country_name) {
        name = this.invoice.bank_detail.location_data.country_name;
      } else {
        this.setCountry();
        name = this.invoice.bank_detail.location_data.country_name;
      }
      return name;
    },

    invoiceCountry: {
      get() {
        if (!this.invoice.bank_detail.location_data.country_id) {
          return '';
        }

        return this.invoice.bank_detail.location_data.country_name;
      },
      set(country) {
        this.invoice.bank_detail.location_data.country_id = country.id;
        this.invoice.bank_detail.location_data.country_name = country.name;
        this.syncUpdateData('bank_detail.location_data.country_name', country.name);

        this.matchedRegion('country');
        this.getRegions();
      },
    },

    invoiceRegion: {
      get() {
        if (!this.invoice.bank_detail.location_data.region_id
          && !this.invoice.bank_detail.location_data.region_name) {
          return '';
        }

        return this.invoice.bank_detail.location_data.region_name ? this.invoice.bank_detail.location_data.region_name : '-';
      },
      set(region) {
        if (typeof region === 'object') {
          this.invoice.bank_detail.location_data.region_id = region.id;
          this.invoice.bank_detail.location_data.region_name = region.name;
          this.syncUpdateData('bank_detail.location_data.region_id', region.id);
          this.syncUpdateData('bank_detail.location_data.region_name', region.name);
          this.getCities();
        } else if (typeof region === 'string') {
          this.invoice.bank_detail.location_data.region_id = null;
          this.invoice.bank_detail.location_data.region_name = region;
          this.syncUpdateData('bank_detail.location_data.region_id', null);
          this.syncUpdateData('bank_detail.location_data.region_name', region);
          this.cities = [];
        }
        this.matchedRegion('region');
      },
    },

    invoiceCity: {
      get() {
        if (!this.invoice.bank_detail.location_data.city_id
          && !this.invoice.bank_detail.location_data.city_name) {
          return '';
        }

        return this.invoice.bank_detail.location_data.city_name ? this.invoice.bank_detail.location_data.city_name : '-';
      },
      set(city) {
        if (typeof city === 'object') {
          this.invoice.bank_detail.location_data.city_id = city.id;
          this.invoice.bank_detail.location_data.city_name = city.name;
          this.syncUpdateData('bank_detail.location_data.city_id', city.id);
          this.syncUpdateData('bank_detail.location_data.city_name', city.name);
        } else if (typeof city === 'string') {
          this.invoice.bank_detail.location_data.city_id = null;
          this.invoice.bank_detail.location_data.city_name = city;
          this.syncUpdateData('bank_detail.location_data.city_id', null);
          this.syncUpdateData('bank_detail.location_data.city_name', city);
        }
      },
    },
  },

  methods: {
    setCountry() {
      this.invoice.bank_detail.location_data.country_id = this
        .invoice.beneficiary.location_data.country_id;
      this.invoice.bank_detail.location_data.country_name = this
        .invoice.beneficiary.location_data.country_name;
    },

    async clickEdit() {
      if (this.edit) {
        await this.saveSettings();
      } else {
        this.edit = !this.edit;
        this.getRegions();
        this.getCities();
      }
    },

    matchedRegion(type) {
      // If location_data changed, lower location_data will be cleaned with updated data
      let updated = [];
      if (Object.keys(this.updateData)[0] === 'bank_detail'
          && Object.keys(this.updateData.bank_detail)[0] === 'location_data') {
        updated = Object.keys(this.updateData.bank_detail.location_data);
      }

      if (['region', 'country'].includes(type)) {
        this.invoice.bank_detail.location_data.city_id = 0;
        this.invoice.bank_detail.location_data.city_name = '';

        if (updated.includes('city_id')) {
          delete this.updateData.bank_detail.location_data.city_id;
        }
      }

      if (type === 'country') {
        this.invoice.bank_detail.location_data.region_id = 0;
        this.invoice.bank_detail.location_data.region_name = '';

        if (updated.includes('region_id')) {
          delete this.updateData.bank_detail.location_data.region_id;
        }
      }
    },

    async getRegions() {
      if (this.invoice.bank_detail.location_data.country_id) {
        try {
          const response = await api.getRegionList(
            this.invoice.bank_detail.location_data.country_id,
          );

          if (response.data) {
            this.regions = response.data.sort((a, b) => {
              const one = (a.name).toLowerCase();
              const two = (b.name).toLowerCase();
              let result = 0;

              if (one > two) {
                result = 1;
              }
              if (one < two) {
                result = -1;
              }
              if (one === two) {
                result = 0;
              }

              return result;
            });
          }
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }
    },

    async getCities() {
      if (this.invoice.bank_detail.location_data.region_id) {
        try {
          const response = await api.getCityList(this.invoice.bank_detail.location_data.region_id);

          if (response.data) {
            this.cities = response.data.sort((a, b) => {
              const one = (a.name).toLowerCase();
              const two = (b.name).toLowerCase();
              let result = 0;

              if (one > two) {
                result = 1;
              }
              if (one < two) {
                result = -1;
              }
              if (one === two) {
                result = 0;
              }

              return result;
            });
          }
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }
    },

    syncUpdateData(field, v) {
      const value = v;
      this.apiRuleClear(field);

      let last;
      this.updateData = field.split('.')
        .reduce((obj, val, index, arr) => {
          const o = obj;
          if (typeof last === 'object') {
            if (index === arr.length - 1) {
              last[val] = value;
            } else if (!last[val]) {
              last[val] = {};
            }
            last = last[val];
          } else {
            if (!o[val]) {
              o[val] = {};
            }
            last = o[val];
          }

          return o;
        }, this.updateData);
    },

    async saveSettings() {
      this.$refs.settingsForm.validate(async (valid, fields) => {
        if (valid) {
          try {
            this.$emit('update:loading', true);
            if (Object.entries(this.updateData).length > 0) {
              if (this.updateData && this.updateData.bank_detail
                && this.updateData.bank_detail.location_data) {
                this.updateData.bank_detail.location_data = this.invoice.bank_detail.location_data;
              }
              let response;
              if (this.roleType === 'coach') {
                if (this.requestMode === 'post') {
                  response = await api.postInvoiceDetail(this.updateData);
                } else {
                  response = await api.patchInvoiceDetail(this.updateData);
                }
              }
              if (response.data) {
                this.updateData = {};
                this.$emit('update:requestMode', 'patch');
                // this.$emit('update:invoice', response.data);
              }
              this.$emit('updatedFetch', response.data);
              this.$message.success(this.$t('coach.invoice.settings.invoiceSaved'));
            }
            this.edit = false;
          } catch (err) {
            console.log(err);
            if (err.field) {
              this.apiRules = err.field;
              if (!this.isErrorMatchingField(this.invoice.bank_detail)) {
                this.$message.error(this.apiErrorMsg);
              }
            } else if (err.msg) {
              this.$message.error(err.msg);
            }
          } finally {
            this.$emit('update:loading', false);
          }
        } else {
          Object.keys(fields)
            .forEach((item, index) => {
              if (index === 0) {
                if (fields[item][0].message) {
                  console.log(`${item}: ${fields[item][0].message}`);
                  // this.$message.error(`${item}: ${fields[item][0].message}`);
                }
              }
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  #bankSection {
    .labelTooltip {
      position: absolute;
      left: 65px;
      top: 3px;
    }

    .emptyPlaceholder {
      position: relative;
      width: 100%;
      display: inline-block;
      font-size: 16px;
    }

    .title {
      margin-bottom: 12px;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.noMargin {
        margin-bottom: 0;
      }

      .left {
        font-size: 16px;

        .thinTitle {
          color: var(--themeThinTitleColor);
          font-style: italic;
          font-weight: normal;
          padding: 0 4px;
        }
      }
    }

    .warning {
      color: #f56c6c;
      font-size: 14px;
    }
  }
</style>

<style lang="scss">
  #bankSection {
    .el-form-item__content {
      font-size: 16px;
    }

    .el-form-item {
      .el-form-item__label {
        font-size: 14px;
        margin-bottom: 10px;
        font-weight: bold;
        text-align: left;
      }

      .el-form-item__content {
        font-size: 16px;
      }
    }

    .el-input {
      &.is-disabled input {
        border-bottom-width: 0;
        cursor: initial;
        padding-left: 0;
      }

      &.emailInput {
        input {
          text-overflow: ellipsis;
        }
      }
    }

    .el-select {
      width: 100%;

      .el-input__inner {
        border-radius: 20px;
        background-color: white;
        color: black;
      }
    }
  }
</style>
